/**
 * Utilitaires pour la gestion des URLs et du routage
 */
import { NgapAct } from '../types/ngap';
import { logger } from './logger';

// Liste des lettres clés valides pour la validation
export const VALID_LETTER_KEYS = [
  'AMK', 'APM', 'ARL', 'DRA', 'NMI', 'PLL', 'RAB', 'RAM', 'RAO', 'RAV',
  'RIC', 'RIM', 'RPB', 'RPE', 'RSC', 'RSM', 'TER', 'VIC', 'VIM', 'VSC', 'VSM'
] as const;

/**
 * Valide si une lettre clé est valide
 */
export function isValidLetterKey(letterKey: string): boolean {
  if (!letterKey || typeof letterKey !== 'string') {
    return false;
  }
  
  const normalized = letterKey.toUpperCase();
  return VALID_LETTER_KEYS.includes(normalized as any);
}

/**
 * Normalise une lettre clé (conversion en majuscules)
 */
export function normalizeLetterKey(letterKey: string): string {
  return letterKey.toUpperCase();
}

/**
 * Génère l'URL pour une lettre clé spécifique
 */
export function generateLetterKeyUrl(letterKey: string): string {
  const normalized = normalizeLetterKey(letterKey);
  return `/cotations/actes/${normalized.toLowerCase()}`;
}

/**
 * Extrait la lettre clé depuis une URL de cotation
 */
export function extractLetterKeyFromUrl(pathname: string): string | null {
  console.log('🔍 extractLetterKeyFromUrl called with pathname:', pathname);
  
  // Updated regex to match the new URL format /cotations/actes/:letter_key
  const match = pathname.match(/^\/cotations\/actes\/([a-zA-Z]{2,4})$/);
  console.log('🔍 extractLetterKeyFromUrl regex match result:', match);
  
  if (!match) return null;
  
  const letterKey = normalizeLetterKey(match[1]);
  const isValid = isValidLetterKey(letterKey);
  
  console.log('🔍 extractLetterKeyFromUrl validation:', {
    extractedKey: letterKey,
    isValid,
    finalResult: isValid ? letterKey : null
  });
  
  return isValid ? letterKey : null;
}

/**
 * Génère les métadonnées SEO pour une page de cotation
 */
export function generateCotationPageMeta(letterKey: string) {
  const normalized = normalizeLetterKey(letterKey);
  
  return {
    title: `Cotations ${normalized} - NGAP Kinésithérapie | Tarifs et Revalorisations`,
    description: `Découvrez toutes les cotations ${normalized} pour kinésithérapeutes avec les tarifs actualisés, les revalorisations prévues et les conditions d'application.`,
    keywords: `${normalized}, cotation ${normalized}, NGAP ${normalized}, kinésithérapie, tarifs, revalorisation`,
    canonical: `https://ngap-kine.fr/cotations/actes/${normalized.toLowerCase()}`
  };
}

/**
 * Génère le breadcrumb pour une page de cotation
 */
export function generateBreadcrumb(letterKey?: string, act?: NgapAct) {
  const breadcrumb = [
    { label: 'Accueil', url: '/' }
  ];
  
  if (letterKey) {
    const normalized = normalizeLetterKey(letterKey);
    breadcrumb.push({
      label: `Cotations ${normalized}`,
      url: `/cotations/actes/${normalized.toLowerCase()}`
    });
    
    if (act) {
      breadcrumb.push({
        label: `${normalized} ${act.cotation_valeur} - ${act.libelle}`,
        url: generateActUrl(act)
      });
    }
  }
  
  return breadcrumb;
}

/**
 * Génère un slug à partir d'un texte (libellé)
 * Convertit en lowercase, supprime les accents, remplace les espaces par des tirets
 */
export function generateSlug(text: string): string {
  if (!text) return '';
  
  return text
    .toLowerCase()
    // Supprimer les accents
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Remplacer les caractères spéciaux et espaces par des tirets
    .replace(/[^a-z0-9\s-]/g, '')
    // Remplacer les espaces multiples par un seul espace
    .replace(/\s+/g, ' ')
    .trim()
    // Remplacer les espaces par des tirets
    .replace(/\s/g, '-')
    // Supprimer les tirets multiples
    .replace(/-+/g, '-')
    // Supprimer les tirets en début et fin
    .replace(/^-+|-+$/g, '');
}

/**
 * Génère un slug à partir d'un texte (libellé) - alias pour compatibilité
 */
export function generateLibelleSlug(text: string): string {
  return generateSlug(text);
}

/**
 * Génère l'URL pour un hashtag de pathologie
 */
export function generateHashtagUrl(hashtag: string): string {
  const slug = generateSlug(hashtag);
  return `/cotations/hashtag/${slug}`;
}

/**
 * Dés-slugifie un hashtag (convertit un slug en hashtag lisible)
 */
export function deslugifyHashtag(slug: string): string {
  if (!slug) return '';
  
  return slug
    // Remplacer les tirets par des espaces
    .replace(/-/g, ' ')
    // Capitaliser la première lettre de chaque mot
    .replace(/\b\w/g, letter => letter.toUpperCase())
    .trim();
}

/**
 * Formate une valeur de cotation pour un slug (remplace la virgule par un tiret)
 */
export function formatCotationValueForSlug(value: string): string {
  if (!value) return '';
  return value.replace(',', '-').replace('.', '-');
}

/**
 * Obtient la valeur de cotation spécifique pour un type donné
 */
export function getActCotationValueForType(
  act: NgapAct,
  cotationType: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027'
): string | null {
  switch (cotationType) {
    case 'current':
      return act.cotation_valeur || null;
    case 'janv2026':
      return act.cotation_valeur_janv_2026 || null;
    case 'may2026':
      return act.cotation_valeur_may_2026 || null;
    case 'sept2026':
      return act.cotation_valeur_sept_2026 || null;
    case 'july2027':
      return act.cotation_valeur_july_2027 || null;
    default:
      return null;
  }
}

/**
 * Génère un slug pour les pages de cotation spécifiques
 */
export function generateCotationValueSlug(
  act: NgapAct,
  cotationType: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027'
): string | null {
  if (!act.lettre_cle) return null;

  const cotationValue = getActCotationValueForType(act, cotationType);
  if (!cotationValue) return null;

  const letterKey = act.lettre_cle.toLowerCase();
  const formattedValue = formatCotationValueForSlug(cotationValue);

  let suffix = '';
  switch (cotationType) {
    case 'janv2026':
      suffix = '-janv-2026';
      break;
    case 'may2026':
      suffix = '-may-2026';
      break;
    case 'sept2026':
      suffix = '-sept-2026';
      break;
    case 'july2027':
      suffix = '-july-2027';
      break;
    default:
      suffix = '';
  }

  return `${letterKey}-${formattedValue}${suffix}`;
}

/**
 * Parse un slug de cotation pour extraire les informations
 */
export function parseCotationSlug(cotationSlug: string): {
  lettre_cle: string | null;
  base_cotation_valeur: string | null;
  cotation_type: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027';
} | null {
  if (!cotationSlug) return null;

  // Patterns pour différents types de cotation
  const patterns = [
    { regex: /^([a-z]{2,4})-(\d+(?:-\d+)?)-july-2027$/, type: 'july2027' as const },
    { regex: /^([a-z]{2,4})-(\d+(?:-\d+)?)-sept-2026$/, type: 'sept2026' as const },
    { regex: /^([a-z]{2,4})-(\d+(?:-\d+)?)-may-2026$/, type: 'may2026' as const },
    { regex: /^([a-z]{2,4})-(\d+(?:-\d+)?)-janv-2026$/, type: 'janv2026' as const },
    { regex: /^([a-z]{2,4})-(\d+(?:-\d+)?)$/, type: 'current' as const }
  ];
  
  for (const pattern of patterns) {
    const match = cotationSlug.match(pattern.regex);
    if (match) {
      const lettre_cle = match[1].toUpperCase();
      const base_cotation_valeur = match[2].replace('-', ','); // Reconvertir le tiret en virgule
      
      return {
        lettre_cle,
        base_cotation_valeur,
        cotation_type: pattern.type
      };
    }
  }
  
  return null;
}

/**
 * Génère l'URL pour une page de cotation spécifique
 */
export function generateCotationPageUrl(
  act: NgapAct,
  cotationType: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027'
): string | null {
  const slug = generateCotationValueSlug(act, cotationType);
  if (!slug) return null;
  
  return `/cotations/${slug}`;
}

/**
 * Trouve un acte par son slug de cotation
 */
export function findActByCotationSlug(acts: NgapAct[], cotationSlug: string): {
  act: NgapAct | null;
  cotationType: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027';
} | null {
  const parsed = parseCotationSlug(cotationSlug);
  if (!parsed) return null;
  
  const { lettre_cle, base_cotation_valeur, cotation_type } = parsed;
  
  console.log(`🔍 Searching for act with letterKey: ${lettre_cle}, cotationValue: ${base_cotation_valeur}, type: ${cotation_type}`);
  console.log(`📊 Total acts to search through: ${acts.length}`);
  
  const foundAct = acts.find(act => {
    console.log(`  🔄 Checking act ID ${act.ID}: letterKey="${act.lettre_cle}", cotationValue="${act.cotation_valeur}"`);
    
    if (!act.lettre_cle || act.lettre_cle.toUpperCase() !== lettre_cle) {
      console.log(`    ❌ Letter key mismatch: "${act.lettre_cle}" !== "${lettre_cle}"`);
      return false;
    }
    
    const expectedValue = getActCotationValueForType(act, cotation_type);
    console.log(`    📋 Expected value for type "${cotation_type}": "${expectedValue}"`);
    
    if (!expectedValue) return false;
    
    // Convertir les valeurs en nombres pour une comparaison numérique précise
    const expectedNumber = parseFloat(expectedValue.replace(',', '.'));
    const baseNumber = parseFloat(base_cotation_valeur.replace(',', '.'));
    
    console.log(`    🔢 Numeric comparison: expectedNumber=${expectedNumber}, baseNumber=${baseNumber}`);
    
    // Vérifier que les deux valeurs sont des nombres valides
    if (isNaN(expectedNumber) || isNaN(baseNumber)) {
      console.log(`  ❌ Invalid number conversion: expected="${expectedValue}" -> ${expectedNumber}, base="${base_cotation_valeur}" -> ${baseNumber}`);
      return false;
    }
    
    // Comparer les valeurs numériques avec une tolérance pour les erreurs de précision
    const isMatch = Math.abs(expectedNumber - baseNumber) < 0.001;
    
    console.log(`    ✅ Final comparison result: ${isMatch ? 'MATCH FOUND!' : 'NO MATCH'}`);
    
    return isMatch;
  });
  
  if (foundAct) {
    console.log(`✅ Found act: ID ${foundAct.ID}, type: ${cotation_type}`);
    return { act: foundAct, cotationType: cotation_type };
  }
  
  console.log(`❌ FINAL RESULT: No act found for ${lettre_cle}-${base_cotation_valeur}-${cotation_type}`);
  return null;
}

/**
 * Génère l'URL complète pour un acte spécifique.
 * Inclut la valeur de cotation dans le slug pour différencier des libellés identiques.
 */
export function generateActUrl(act: NgapAct): string {
  if (!act.lettre_cle || !act.libelle) {
    return '/';
  }

  const letterKey = normalizeLetterKey(act.lettre_cle).toLowerCase();
  const libelleSlug = generateSlug(act.libelle);
  const valueSuffix = act.cotation_valeur
    ? `-${formatCotationValueForSlug(act.cotation_valeur.trim())}`
    : '';

  return `/cotations/${letterKey}/${libelleSlug}${valueSuffix}`;
}

/**
 * Parse une URL d'acte pour extraire la lettre clé et le slug
 */
export function parseActUrl(pathname: string): { letterKey: string | null; slug: string | null } {
  const match = pathname.match(/^\/cotations\/([a-zA-Z]{2,4})\/(.+)$/);
  
  if (!match) {
    return { letterKey: null, slug: null };
  }
  
  const letterKey = normalizeLetterKey(match[1]);
  const slug = match[2];
  
  return {
    letterKey: isValidLetterKey(letterKey) ? letterKey : null,
    slug: slug || null
  };
}

/**
 * Trouve un acte par lettre clé et slug
 */
export function findActBySlug(acts: NgapAct[], letterKey: string, slug: string): NgapAct | null {
  logger.debug(`Searching for act with letterKey: ${letterKey}, slug: ${slug}`);

  const candidates = acts.filter(
    act => act.lettre_cle && act.lettre_cle.toUpperCase() === letterKey.toUpperCase()
  );

  const exactMatch = candidates.find(act => {
    const libelleSlug = generateSlug(act.libelle);
    const valueSuffix = act.cotation_valeur
      ? `-${formatCotationValueForSlug(act.cotation_valeur.trim())}`
      : '';
    return `${libelleSlug}${valueSuffix}` === slug;
  });

  if (exactMatch) {
    logger.debug(`MATCH (libelle+value) found: Act ID ${exactMatch.ID}`);
    return exactMatch;
  }

  const legacyMatches = candidates.filter(act => generateSlug(act.libelle) === slug);
  if (legacyMatches.length > 0) {
    logger.debug(`Legacy MATCH (libelle only) found: Act ID ${legacyMatches[0].ID}`);
    return legacyMatches[0];
  }

  logger.debug(`NO MATCH FOUND for letterKey: ${letterKey}, slug: ${slug}`);
  return null;
}

/**
 * Génère les métadonnées SEO pour une page d'acte spécifique
 */
export function generateActPageMeta(
  act: NgapAct,
  cotationType?: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027',
  canonicalUrl?: string
) {
  const letterKey = act.lettre_cle?.toUpperCase() || '';
  const baseUrl = 'https://ngap-kine.fr';

  // Utiliser l'URL canonique si fournie, sinon l'URL basée sur le libellé
  const actUrl = canonicalUrl || generateActUrl(act);

  // Obtenir la valeur de cotation spécifique si un type est fourni
  const cotationValue = cotationType ?
    getActCotationValueForType(act, cotationType) || act.cotation_valeur :
    act.cotation_valeur;

  // Générer un suffixe pour le titre si c'est une revalorisation
  let dateSuffix = '';
  if (cotationType && cotationType !== 'current') {
    switch (cotationType) {
      case 'janv2026':
        dateSuffix = ' - Janvier 2026';
        break;
      case 'may2026':
        dateSuffix = ' - Mai 2026';
        break;
      case 'sept2026':
        dateSuffix = ' - Septembre 2026';
        break;
      case 'july2027':
        dateSuffix = ' - Juillet 2027';
        break;
    }
  }
  
  // Informations sur les revalorisations pour enrichir la description
  const hasRevalorisations = act.revalo_janv_2026 || act.revalo_may_2026 ||
                            act.revalo_sept_2026 || act.revalo_july_2027;
  const revaloText = hasRevalorisations ? ' Revalorisations prévues 2025-2027.' : '';
  
  // Informations sur le référentiel
  const referentielText = act.soumis_referentiel ? ' Soumis au référentiel.' : ' Hors référentiel.';
  
  return {
    title: `${letterKey} ${cotationValue} - ${act.libelle}${dateSuffix} | NGAP Kiné`,
    description: `Cotation ${letterKey} ${cotationValue} : ${act.libelle}.${referentielText}${revaloText} Tarifs métropole et DROM actualisés selon le référentiel NGAP.${dateSuffix ? ` Tarifs ${dateSuffix.toLowerCase()}.` : ''}`,
    keywords: `${letterKey}, ${letterKey} ${cotationValue}, ${act.libelle}, NGAP, kinésithérapie, tarifs, cotation, ${act.theme || 'rééducation'}${hasRevalorisations ? ', revalorisation' : ''}${dateSuffix ? `, ${dateSuffix.toLowerCase()}` : ''}`,
    canonical: `${baseUrl}${actUrl}`,
    ogTitle: `${letterKey} ${cotationValue} - ${act.libelle}${dateSuffix} | NGAP Kiné`,
    ogDescription: `Cotation ${letterKey} ${cotationValue} : ${act.libelle}.${referentielText} Tarifs actualisés${hasRevalorisations ? ' et revalorisations prévues' : ''}.${dateSuffix ? ` Tarifs ${dateSuffix.toLowerCase()}.` : ''}`,
    ogUrl: `${baseUrl}${actUrl}`,
    twitterTitle: `${letterKey} ${cotationValue} - ${act.libelle}${dateSuffix}`,
    twitterDescription: `Cotation ${letterKey} ${cotationValue}${hasRevalorisations ? ' avec revalorisations prévues' : ''} - Tarifs NGAP actualisés.${dateSuffix ? ` ${dateSuffix}.` : ''}`
  };
}

/**
 * Génère un slug à partir d'un thème
 */
export function generateThemeSlug(theme: string): string {
  return generateSlug(theme);
}

/**
 * Dés-slugifie un thème (convertit un slug en thème lisible)
 */
export function deslugifyTheme(slug: string): string {
  if (!slug) return '';
  
  return slug
    // Remplacer les tirets par des espaces
    .replace(/-/g, ' ')
    // Capitaliser la première lettre de chaque mot
    .replace(/\b\w/g, letter => letter.toUpperCase())
    .trim();
}

/**
 * Génère l'URL pour un thème spécifique
 */
export function generateThemeUrl(theme: string): string {
  const slug = generateThemeSlug(theme);
  return `/themes/${slug}`;
}

/**
 * Génère les métadonnées SEO pour une page de thème
 */
export function generateThemePageMeta(theme: string, acts: NgapAct[]) {
  const normalizedTheme = theme.charAt(0).toUpperCase() + theme.slice(1).toLowerCase();
  const actsForTheme = acts.filter(act => 
    act.theme && act.theme.toLowerCase() === theme.toLowerCase()
  );
  const actsCount = actsForTheme.length;
  
  // Extraire quelques exemples d'actes pour la description
  const exampleActs = actsForTheme.slice(0, 3).map(act => 
    `${act.lettre_cle} ${act.cotation_valeur}`
  ).join(', ');
  
  const baseUrl = 'https://ngap-kine.fr';
  const themeSlug = generateThemeSlug(theme);
  
  return {
    title: `${normalizedTheme} - NGAP Kinésithérapie | ${actsCount} actes disponibles`,
    description: `Découvrez les ${actsCount} actes de ${normalizedTheme.toLowerCase()} pour kinésithérapeutes${exampleActs ? ` (${exampleActs}...)` : ''}. Tarifs actualisés, revalorisations 2025-2027 et conditions d'application selon le référentiel NGAP.`,
    keywords: `${normalizedTheme}, ${normalizedTheme.toLowerCase()}, NGAP, kinésithérapie, tarifs, revalorisation, physiothérapie, référentiel${exampleActs ? `, ${exampleActs}` : ''}`,
    canonical: `${baseUrl}/themes/${themeSlug}`,
    ogTitle: `${normalizedTheme} - NGAP Kinésithérapie (${actsCount} actes)`,
    ogDescription: `Consultez les ${actsCount} actes de ${normalizedTheme.toLowerCase()} avec tarifs actualisés et revalorisations prévues pour kinésithérapeutes.`,
    ogUrl: `${baseUrl}/themes/${themeSlug}`,
    twitterTitle: `${normalizedTheme} - NGAP Kiné`,
    twitterDescription: `${actsCount} actes de ${normalizedTheme.toLowerCase()} actualisés pour kinésithérapeutes avec revalorisations 2025-2027.`
  };
}

/**
 * Génère le label formaté du code NGAP (lettre clé + cotation valeur)
 * 
 * @param act - L'acte NGAP
 * @param cotationType - Le type de cotation à utiliser (optionnel)
 * @returns Le label formaté du code NGAP (ex: "NMI 10", "AMK 7.3")
 */
export function generateNgapCodeLabel(
  act: NgapAct,
  cotationType?: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027'
): string {
  const letterKey = act.lettre_cle || '';

  let cotationValue: string;

  if (cotationType && cotationType !== 'auto') {
    cotationValue = getActCotationValueForType(act, cotationType) || act.cotation_valeur || '';
  } else {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    if (act.revalo_july_2027 && act.cotation_valeur_july_2027 && todayStr >= '2027-07-01') {
      cotationValue = act.cotation_valeur_july_2027;
    } else if (act.revalo_sept_2026 && act.cotation_valeur_sept_2026 && todayStr >= '2026-09-01') {
      cotationValue = act.cotation_valeur_sept_2026;
    } else if (act.revalo_may_2026 && act.cotation_valeur_may_2026 && todayStr >= '2026-05-28') {
      cotationValue = act.cotation_valeur_may_2026;
    } else if (act.revalo_janv_2026 && act.cotation_valeur_janv_2026 && todayStr >= '2026-01-01') {
      cotationValue = act.cotation_valeur_janv_2026;
    } else {
      cotationValue = act.cotation_valeur || '';
    }
  }

  if (!letterKey && !cotationValue) {
    return 'Code non disponible';
  }

  if (!letterKey) {
    return cotationValue;
  }

  if (!cotationValue) {
    return letterKey;
  }

  return `${letterKey} ${cotationValue}`;
}

/**
 * Normalise les valeurs numériques pour la recherche en remplaçant les points par des virgules
 * 
 * @param value - La valeur à normaliser
 * @returns La valeur normalisée avec des virgules au lieu des points
 */
export function normalizeNumericValueForSearch(value: string): string {
  if (!value || typeof value !== 'string') {
    return '';
  }
  
  return value.replace(/\./g, ',');
}